import { vec3 } from "_src/libs/gl-matrix";
import intersectEvaluator from "_src/engine/intersect_evaluator";
import minmaxUtils from "_src/engine/utility/minmax";

const MAX_DEPTH = 8;
const MAX_FACE_NUM = 50;

const _vec3Tmp = vec3.create();
const _vec3Tmp2 = vec3.create();

const _stack = [];

export default class Octant {

  constructor(parent) {

    this._AABB = [Infinity, Infinity, Infinity, -Infinity, -Infinity, -Infinity];

    this._offset = 0;
    this._length = 0;

    this._children = [];

    this._parent = parent;

  }

  buildFromBuffer(bboxBuffer, sizeBuffer) {

    _stack.push(this);
    const indStack = [0];
    const levels = [0];

    let currIndex = 0;

    const oneThird = 1 / 3;

    while (_stack.length) {

      const cell = _stack.pop();
      const cellInd = indStack.pop();
      const cellDepth = levels.pop();

      cell._offset = sizeBuffer[cellInd * 2];
      cell._length = sizeBuffer[cellInd * 2 + 1];

      cell.setAABBFromBuffer(cellInd, bboxBuffer);

      const facesNum = cell._length * oneThird;

      if (facesNum > MAX_FACE_NUM && cellDepth < MAX_DEPTH) {

        cell.createChildren();

        _stack.push.apply(_stack, cell._children);

        indStack.push(currIndex + 1, currIndex + 2, currIndex + 3, currIndex + 4,
                   currIndex + 5, currIndex + 6, currIndex + 7, currIndex + 8);

        const nextLevelDepth = cellDepth + 1;
        levels.push(nextLevelDepth, nextLevelDepth, nextLevelDepth, nextLevelDepth,
                    nextLevelDepth, nextLevelDepth, nextLevelDepth, nextLevelDepth);

        currIndex += 8;

      }

    }

  }

  createChildren() {

    for (let i = 0; i < 8; ++i) {

      const child = new Octant(this);
      this._children.push(child);

    }

  }

  setAABBFromBuffer(selfInd, bboxBuffer) {

    const bb = this._AABB;

    const offset = 6 * selfInd;

    bb[0] = bboxBuffer[offset];
    bb[1] = bboxBuffer[offset + 1];
    bb[2] = bboxBuffer[offset + 2];
    bb[3] = bboxBuffer[offset + 3];
    bb[4] = bboxBuffer[offset + 4];
    bb[5] = bboxBuffer[offset + 5];

  }

  castRay(from, to, intersector, dest) {

    const distances = _vec3Tmp;
    const toInv = vec3.inverse(_vec3Tmp2, to);

    const result = Octant.intersection;
    const nodes = [];

    if (this._checkRayIntersect(from, toInv, distances)) {
      _stack.push(this);
    }

    while (_stack.length) {

      const cell = _stack.pop();

      if (cell._children.length) {

        nodes.length = 0;

        for (let i = 0; i < cell._children.length; ++i) {

          const child = cell._children[i];

          distances[0] = Infinity;

          if (child._checkRayIntersect(from, toInv, distances)) {

            nodes.push({
              dist : distances[0],
              node : child
            });

          }

        }

        nodes.sort((a, b) => {
          return b.dist - a.dist;
        });

        for (let i = 0; i < nodes.length; ++i) {
          _stack.push(nodes[i].node);
        }

      } else if (cell._length) {

        const hit = intersector(from, to, cell._offset, cell._length, result);

        if (hit) {

          _stack.length = 0;

          vec3.copy(dest, result.point);

          return result.distance;

        }

      }

    }

    return -1;

  }

  intersectRay(from, to, indices, positions, tlim, dest) {

    const distances = _vec3Tmp;
    const toInv = vec3.inverse(_vec3Tmp2, to);

    const result = Octant.intersection;
    const nodes = [];

    if (this._checkLineIntersect(from, toInv, tlim, distances)) {
      _stack.push(this);
    }

    while (_stack.length) {

      const cell = _stack.pop();

      if (cell._children.length) {

        nodes.length = 0;

        for (let i = 0; i < cell._children.length; ++i) {

          const child = cell._children[i];
          if (child._checkLineIntersect(from, toInv, tlim, distances)) {
            nodes.push({
              dist : Math.min(Math.abs(distances[0]), Math.abs(distances[1])),
              node : child
            });
          }

        }

        nodes.sort((a, b) => {
          return b.dist - a.dist;
        });

        for (let i = 0; i < nodes.length; ++i) {
          _stack.push(nodes[i].node);
        }

      } else if (cell._length) {

        if (intersectEvaluator.triangleLineIntersection(from, to, indices,
            cell._offset, cell._length, positions, tlim, result)) {

          _stack.length = 0;

          vec3.copy(dest, result.point);
          dest[3] = result.distance;

          return true;

        }

      }

    }

    return false;

  }

  sphereIntersect(center, radius, indices, positions, vertsMask, scale) {

    _stack.push(this);
    const leaves = [];
    const inside = [];
    const squaredRadius = radius * radius;

    while (_stack.length) {

      const cell = _stack.pop();

      if (cell._length == 0) {
        continue;
      }

      const bb = cell._AABB;

      const intersection = intersectEvaluator.AABBSphereIntersect(center, squaredRadius, bb, scale);

      if (intersection == intersectEvaluator.INSIDE) {
        inside.push(cell);
      } else if (intersection == intersectEvaluator.INTERSECTION) {

        if (cell._children.length) {
          _stack.push.apply(_stack, cell._children);
        } else {
          leaves.push(cell);
        }

      }

    }

    let numSelected = 0;

    for (let i = 0; i < leaves.length; ++i) {

      const leaf = leaves[i];
      const offset = leaf._offset;
      const length = leaf._length;

      numSelected += intersectEvaluator.markVerticesInsideSphere(center, squaredRadius,
                          indices, offset, length, positions, vertsMask, scale);

    }

    for (let i = 0; i < inside.length; ++i) {

      const offset = inside[i]._offset;
      const length = inside[i]._length;

      numSelected += addAll(indices, offset, length, vertsMask);

      // inside[i].getLeaves(leaves);

    }

    return numSelected;

  }

  recalculateBoundings(indices, positions) {

    const leaves = [];

    _stack.push(this);

    while (_stack.length) {

      const cell = _stack.pop();

      if (cell._length == 0) {
        continue;
      }

      if (cell._children.length) {
        _stack.push.apply(_stack, cell._children);
        cell.initBoundings();
      } else {
        leaves.push(cell);
      }

    }

    leaves.sort((a, b) => {
      return a._offset - b._offset;
    });

    for (let i = 0; i < leaves.length; ++i) {

      const leaf = leaves[i];

      leaf._recalculateBoundings(indices, positions);
      leaf.expandAABBLoose();

    }

  }

  _checkRayIntersect(from, toInv, dest) {

    const fromX = from[0];
    const fromY = from[1];
    const fromZ = from[2];

    const dirX = toInv[0];
    const dirY = toInv[1];
    const dirZ = toInv[2];

    const bb = this._AABB;

    const t1 = (bb[0] - fromX) * dirX;
    const t3 = (bb[1] - fromY) * dirY;
    const t5 = (bb[2] - fromZ) * dirZ;
    const t2 = (bb[3] - fromX) * dirX;
    const t4 = (bb[4] - fromY) * dirY;
    const t6 = (bb[5] - fromZ) * dirZ;

    const tmin = Math.max(Math.min(t1, t2), Math.min(t3, t4), Math.min(t5, t6));
    const tmax = Math.min(Math.max(t1, t2), Math.max(t3, t4), Math.max(t5, t6));

    dest[0] = tmin;
    dest[1] = tmax;

    return tmax >= 0 && tmin <= tmax;

  }

  _checkLineIntersect(from, toInv, tlim, dest) {

    const fromX = from[0];
    const fromY = from[1];
    const fromZ = from[2];

    const dirX = toInv[0];
    const dirY = toInv[1];
    const dirZ = toInv[2];

    const bb = this._AABB;

    const t1 = (bb[0] - fromX) * dirX;
    const t3 = (bb[1] - fromY) * dirY;
    const t5 = (bb[2] - fromZ) * dirZ;
    const t2 = (bb[3] - fromX) * dirX;
    const t4 = (bb[4] - fromY) * dirY;
    const t6 = (bb[5] - fromZ) * dirZ;

    const tmin = Math.max(Math.min(t1, t2), Math.min(t3, t4), Math.min(t5, t6));
    const tmax = Math.min(Math.max(t1, t2), Math.max(t3, t4), Math.max(t5, t6));

    dest[0] = tmin;
    dest[1] = tmax;

    const inside = tmin * tmax < 0;
    const inlim = Math.min(tmin * tmin, tmax * tmax) <= tlim * tlim;

    return tmin <= tmax && (inlim || inside);

  }

  maskIntersect(indices, positions, vertsMask, maskPixels, depthPixels, width, height, bounding, mvp, near, far, linearDepth) {

    _stack.push(this);

    const leaves = [];
    const inside = [];

    while (_stack.length) {

      const cell = _stack.pop();

      if (cell._length == 0) {
        continue;
      }

      const minmax = cell._AABB;

      const res = intersectEvaluator.maskAABBTest(maskPixels, width, height, bounding, minmax, mvp);

      if (res == intersectEvaluator.INSIDE) {

        inside.push(cell);

      } else if (res == intersectEvaluator.INTERSECTION) {

        if (cell._children.length) {

          _stack.push.apply(_stack, cell._children);

        } else {

          leaves.push(cell);

        }

      }

    }

    let numSelected = 0;

    for (let i = 0; i < leaves.length; ++i) {

      const leaf = leaves[i];
      const offset = leaf._offset;
      const length = leaf._length;

      numSelected += intersectEvaluator.markVerticesInsideMask(maskPixels, depthPixels,
              width, height, indices, offset, length, positions, vertsMask, mvp, near, far, linearDepth);

    }

    for (let i = 0; i < inside.length; ++i) {

      const offset = inside[i]._offset;
      const length = inside[i]._length;

      if (depthPixels) {
        numSelected += intersectEvaluator.markVerticesInsideMask(maskPixels, depthPixels,
                width, height, indices, offset, length, positions, vertsMask, mvp, near, far, linearDepth);
      } else {
        numSelected += addAll(indices, offset, length, vertsMask);
      }

      // inside[i].getLeaves(leaves);

    }

    return numSelected;

  }

  planeIntersect(indices, positions, vertsMask, plane) {

    _stack.length = 0;
    _stack.push(this);

    const leaves = [];
    const inside = [];

    while (_stack.length) {

      const cell = _stack.pop();

      if (cell._length == 0) {
        continue;
      }

      const minmax = cell._AABB;

      const res = intersectEvaluator.planeAABBTest(plane, minmax);

      if (res == intersectEvaluator.INSIDE) {

        inside.push(cell);

      } else if (res == intersectEvaluator.INTERSECTION) {

        if (cell._children.length) {

          _stack.push.apply(_stack, cell._children);

        } else {

          leaves.push(cell);

        }

      }

    }

    let numSelected = 0;

    for (let i = 0; i < leaves.length; ++i) {

      const leaf = leaves[i];
      const offset = leaf._offset;
      const length = leaf._length;

      numSelected += intersectEvaluator.markVerticesBehindPlane(plane,
                          indices, offset, length, positions, vertsMask);

    }

    for (let i = 0; i < inside.length; ++i) {

      const offset = inside[i]._offset;
      const length = inside[i]._length;

      numSelected += addAll(indices, offset, length, vertsMask);

    }

    return numSelected;

  }

  getLeaves(leaves = []) {

    _stack.push(this);

    while (_stack.length) {

      const cell = _stack.pop();

      if (cell._children.length) {
        _stack.push.apply(_stack, cell._children);
      } else {
        leaves.push(cell);
      }

    }

    return leaves;

  }

  initBoundings() {
    minmaxUtils.reset(this._AABB);
  }

  _hasSelection(mask) {

    const first = this._offset;
    const last = first + this._length;

    for (let i = first; i < last; ++i) {
      if (mask[i]) {
        return true;
      }
    }

    return false;

  }

  _recalculateBoundings(indices, positions) {

    const first = this._offset;
    const last = first + this._length;

    this.initBoundings();

    const bbox = this._AABB;
    let minx = bbox[0];
    let miny = bbox[1];
    let minz = bbox[2];
    let maxx = bbox[3];
    let maxy = bbox[4];
    let maxz = bbox[5];

    for (let i = first; i < last; ++i) {

      const v = indices[i];

      const offset = v * 3;

      const x = positions[offset];
      const y = positions[offset + 1];
      const z = positions[offset + 2];

      if (x < minx) { minx = x; }
      else if (x > maxx) { maxx = x; }

      if (y < miny) { miny = y; }
      else if (y > maxy) { maxy = y; }

      if (z < minz) { minz = z; }
      else if (z > maxz) { maxz = z; }

    }

    bbox[0] = minx;
    bbox[1] = miny;
    bbox[2] = minz;
    bbox[3] = maxx;
    bbox[4] = maxy;
    bbox[5] = maxz;

  }

  expandAABBLoose() {

    const bb = this._AABB;
    const minx = bb[0];
    const miny = bb[1];
    const minz = bb[2];
    const maxx = bb[3];
    const maxy = bb[4];
    const maxz = bb[5];

    let parent = this._parent;

    while (parent) {

      const bb = parent._AABB;

      let updated = false;

      if (minx < bb[0]) {

        bb[0] = minx;
        updated = true;

      }

      if (miny < bb[1]) {

        bb[1] = miny;
        updated = true;

      }

      if (minz < bb[2]) {

        bb[2] = minz;
        updated = true;

      }

      if (bb[3] < maxx) {

        bb[3] = maxx;
        updated = true;

      }

      if (bb[4] < maxy) {

        bb[4] = maxy;
        updated = true;

      }

      if (bb[5] < maxz) {

        bb[5] = maxz;
        updated = true;

      }

      parent = updated ? parent._parent : null;

    }

  }

}

function addAll(indices, offset, length, vertsMask) {

  const startInd = offset;
  const finalInd = startInd + length;
  let numSelected = 0;

  for (let i = startInd; i < finalInd; ++i) {

    const ind = indices[i];

    if (vertsMask[ind] === 1) {
      continue;
    }

    vertsMask[ind] = 1;
    ++numSelected;

  }

  return numSelected;

}

Octant.intersection = {

  point : vec3.create(),
  distance : Infinity

}
